import type { ReactNode } from "react";
import { TaskPriority } from "@common/types/task";
import { TASK_PRIORITY_CONFIG } from "@components/tasks/TaskPrioritySelect/taskPriority.constant";
import { BoardCardTag } from "@design-system/Board/BoardCardTag";
import { TagOverflow } from "@design-system/Tag/TagOverflow";
import { VStack } from "@design-system/VStack/VStack";
import { CalendarIcon, ClockIcon } from "@icons";
import playgroundStyles from "./BoardPlayground.module.scss";
import styles from "./BoardCardTagExamples.module.scss";

const {
    Icon: HighPriorityIcon,
    label: highPriorityLabel,
    className: highPriorityClassName,
} = TASK_PRIORITY_CONFIG[TaskPriority.HIGH];

const LATE_WORK_END = (
    <BoardCardTag
        field="Work end"
        tooltip="fieldName"
        detail="3 days late"
        icon={<CalendarIcon />}
    >
        Oct 20, 2026
    </BoardCardTag>
);

const EXAMPLES: ReadonlyArray<{
    id: string;
    label: string;
    name: string;
    tag: ReactNode;
}> = [
    {
        id: "fits",
        label: "Fits: no tooltip",
        name: "Close: Q3 close",
        tag: <BoardCardTag field="Close">Q3 close</BoardCardTag>,
    },
    {
        id: "field-name",
        label: "Value without its field: field name",
        name: "Estimate: 2 h",
        tag: (
            <BoardCardTag
                field="Estimate"
                tooltip="fieldName"
                icon={<ClockIcon />}
            >
                2 h
            </BoardCardTag>
        ),
    },
    {
        id: "truncated",
        label: "Cut off: field and value",
        name: "Close: Monthly bookkeeping for Northwind Holdings",
        tag: (
            <BoardCardTag field="Close">
                Monthly bookkeeping for Northwind Holdings
            </BoardCardTag>
        ),
    },
    {
        id: "icon-only",
        label: "Icon only: field and value",
        name: `Priority: ${highPriorityLabel}`,
        tag: (
            <BoardCardTag
                field="Priority"
                value={highPriorityLabel}
                tooltip="fieldAndValue"
                icon={<HighPriorityIcon className={highPriorityClassName} />}
            />
        ),
    },
    {
        id: "detail",
        label: "With a detail",
        name: "Work end: Oct 20, 2026",
        tag: LATE_WORK_END,
    },
    {
        id: "detail-truncated",
        label: "Cut off, with a detail",
        name: "Work end: Oct 20, 2026",
        tag: <span className={styles.narrow}>{LATE_WORK_END}</span>,
    },
];

const OVERFLOW_ITEMS = EXAMPLES.filter(
    ({ id }) => id !== "detail-truncated",
).map(({ id, name, tag }) => ({ id, label: name, content: tag }));

/** `BoardCardTag` in each tooltip state, then in a `TagOverflow` whose `+N` popover holds the rest. */
export const BoardCardTagExamples = () => (
    <VStack as="section" gap={12}>
        <h3 className={playgroundStyles.controlsHeading}>Card tags</h3>
        {EXAMPLES.map(({ id, label, tag }) => (
            <VStack key={id} gap={6} align="start">
                <span className={playgroundStyles.controlsLabel}>{label}</span>
                {tag}
            </VStack>
        ))}
        <VStack gap={6} align="start">
            <span className={playgroundStyles.controlsLabel}>
                In the +N popover
            </span>
            <TagOverflow items={OVERFLOW_ITEMS} max={2} />
        </VStack>
    </VStack>
);
